import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { IBasket } from "../../../models/catalogue";
import { IReduxState } from "../reducers.interfaces";

interface LoadBasketIfOutdatedResult {
    isOutdated: boolean;
    basket: IBasket | null;
}

/**
 * Check basket timestamp and reload if stale.
 *
 * Compares the stored basket's last modified timestamp with the server's and
 * reloads via the loadBasket thunk only when they differ. A basket that has
 * never been loaded is loaded straight away; a stored "no basket" answer is
 * checked with the same cheap timestamp request as a stored basket, so a
 * visitor without a basket does not pay for a full reload on every call.
 */
export const loadBasketIfOutdated = createAsyncThunk<
    LoadBasketIfOutdatedResult,
    void,
    { state: { checkout: IReduxState } }
>("checkout/loadBasketIfOutdated", async (_, { dispatch, getState }) => {
    const { basket: currentBasket, basket_loaded: isLoaded } =
        getState().checkout.data;

    const reload = async (): Promise<LoadBasketIfOutdatedResult> => {
        // Import dynamically to avoid circular dependency
        const { loadBasket } = await import("./loadBasket");
        const result = await dispatch(loadBasket()).unwrap();
        return {
            isOutdated: true,
            basket: result.basket,
        };
    };

    if (!currentBasket) {
        if (!isLoaded) {
            return reload();
        }
        const data = await basketAPI.getLastModifiedTimestamp();
        return data ? reload() : { isOutdated: false, basket: null };
    }
    if (!currentBasket.id || !currentBasket.last_modified_timestamp) {
        return reload();
    }

    const data = await basketAPI.getLastModifiedTimestamp();
    if (!data) {
        return reload();
    }
    if (!data.last_modified_timestamp) {
        return {
            isOutdated: false,
            basket: currentBasket,
        };
    }

    const currentTimestamp = new Date(
        currentBasket.last_modified_timestamp,
    ).getTime();
    const newTimestamp = new Date(data.last_modified_timestamp).getTime();
    const idsMatch = currentBasket.id === data.id;
    const isOutdated = currentTimestamp < newTimestamp;

    if (currentTimestamp && newTimestamp && idsMatch && !isOutdated) {
        return {
            isOutdated: false,
            basket: currentBasket,
        };
    }

    return reload();
});
